var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ButtonList, DropdownButton, FieldWrapper, Select, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { AudioPlayer } from '../audioPlayer/audioPlayer';
import { getAdapter } from './adapters';
import styles from './announcementPicker.module.scss';
import { RecordDialog } from './recordDialog';
import { UploadDialog } from './uploadDialog';
export var Dialog;
(function (Dialog) {
    Dialog["Default"] = "default";
    Dialog["Record"] = "record";
    Dialog["Upload"] = "upload";
})(Dialog || (Dialog = {}));
export function AnnouncementPicker({ adapter: adapterName, audioPlayerClassName, clearable, className, dropdownProps, fieldProps, selectedAudio: passedSelectedAudio, withRecord = true, disabled, onClear, onDialogChange, onSelect, }) {
    const adapter = useMemo(() => getAdapter(adapterName), [adapterName]);
    const [isLoading, setIsLoading] = useState(false);
    const [audioItems, setAudioItems] = useState([]);
    const initialSelectedAudio = useMemo(() => {
        if (!adapter || !passedSelectedAudio || audioItems.length === 0) {
            return undefined;
        }
        return adapter.getFormattedItem(passedSelectedAudio);
    }, [adapter, passedSelectedAudio, audioItems]);
    const [selectedAudio, setSelectedAudio] = useState(initialSelectedAudio !== null && initialSelectedAudio !== void 0 ? initialSelectedAudio : null);
    const [dialog, setDialog] = useState(Dialog.Default);
    const addNotification = useNotifications();
    useUpdateEffect(() => {
        onDialogChange === null || onDialogChange === void 0 ? void 0 : onDialogChange(dialog);
    }, [dialog, onDialogChange, useUpdateEffect]);
    const handleAudioSelect = useCallback(({ id }) => {
        const selectedAudio = audioItems === null || audioItems === void 0 ? void 0 : audioItems.find((audio) => audio.id === id);
        if (!selectedAudio) {
            return;
        }
        setSelectedAudio(selectedAudio);
        const item = adapter.getItemForCallback(id);
        if (item) {
            onSelect(item, 'select');
        }
    }, [audioItems, setSelectedAudio, onSelect, adapter]);
    const handleClear = useCallback(() => {
        setSelectedAudio(null);
        onClear === null || onClear === void 0 ? void 0 : onClear();
    }, [onClear]);
    const handleUpload = useCallback((type) => (file) => __awaiter(this, void 0, void 0, function* () {
        setIsLoading(true);
        try {
            const url = yield adapter.upload(file);
            const items = yield adapter.getItems();
            const uploadedItem = adapter.getItemFromURL(url);
            setAudioItems(items);
            if (!uploadedItem) {
                return;
            }
            const callbackItem = adapter.getItemForCallback(uploadedItem.id);
            if (callbackItem) {
                onSelect(callbackItem, type === Dialog.Upload ? 'upload' : 'record');
            }
            setSelectedAudio(uploadedItem);
        }
        catch (err) {
            console.error('There was an error while trying to upload the audio file', err);
            addNotification({ text: 'An error occurred when trying to upload the file' }, NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }), [adapter, setIsLoading, setAudioItems, addNotification, onSelect]);
    const onClose = useCallback(() => setDialog(Dialog.Default), []);
    const onRecordClick = useCallback(() => setDialog(Dialog.Record), []);
    const onUploadClick = useCallback(() => setDialog(Dialog.Upload), []);
    const isDefaultOpen = dialog === Dialog.Default;
    const isRecordOpen = dialog === Dialog.Record;
    const isUploadOpen = dialog === Dialog.Upload;
    useEffect(() => {
        if (!initialSelectedAudio) {
            return;
        }
        setSelectedAudio(initialSelectedAudio);
    }, [initialSelectedAudio, setSelectedAudio]);
    useEffect(() => {
        const handleGetAudio = () => __awaiter(this, void 0, void 0, function* () {
            setIsLoading(true);
            try {
                const items = yield adapter.getItems();
                setAudioItems(items);
            }
            catch (err) {
                console.error('Failed to fetch audio items', err);
                addNotification({ text: 'Failed to fetch audio items, please try again' }, NotificationType.Error);
            }
            finally {
                setIsLoading(false);
            }
        });
        handleGetAudio();
    }, [adapter, adapterName, addNotification]);
    return (_jsxs("div", Object.assign({ className: className }, { children: [isRecordOpen && (_jsx(RecordDialog, { onClose: onClose, onUpload: handleUpload(Dialog.Record) })), isUploadOpen && (_jsx(UploadDialog, { onClose: onClose, onUpload: handleUpload(Dialog.Upload) })), isDefaultOpen && (_jsxs(FieldWrapper, Object.assign({ label: "Select an audio file" }, fieldProps, { children: [_jsxs("div", Object.assign({ className: styles.select }, { children: [_jsx(Select, { clearable: clearable, loading: isLoading, disabled: disabled || isLoading, dropdownProps: dropdownProps, placeholder: "No audio selected", selectedItems: selectedAudio ? [selectedAudio === null || selectedAudio === void 0 ? void 0 : selectedAudio.id] : [], items: audioItems, onClear: handleClear, onSelect: handleAudioSelect, searchable: true }), withRecord ? (_jsx(DropdownButton, Object.assign({ className: styles.dropdown, buttonProps: { variant: 'secondary', disabled }, dropdown: _jsxs(ButtonList, { children: [_jsx(ButtonList.Item, Object.assign({ buttonProps: { type: 'button' }, onClick: onRecordClick }, { children: "Record" })), _jsx(ButtonList.Item, Object.assign({ buttonProps: { type: 'button' }, onClick: onUploadClick }, { children: "Upload file" }))] }) }, { children: "New audio file" }))) : (_jsx(Button, Object.assign({ onClick: onUploadClick, size: "large", variant: "secondary" }, { children: "Upload file" })))] })), _jsx(AudioPlayer, { isDownloadable: true, className: classNames(styles.audioPlayer, audioPlayerClassName), disabled: isLoading, src: selectedAudio === null || selectedAudio === void 0 ? void 0 : selectedAudio.url })] })))] })));
}
